import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, RefreshCw, Pencil, Trash2, X, Search, ArrowLeft, Activity, Phone, Mail, MapPin, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CustomerTimeline from "@/components/dashboard/CustomerTimeline";

const BLANK = { name: "", phone: "", email: "", address: "", notes: "" };
const PRIMARY = "#F97316";

const startOpportunity = (c, navigate) => {
  sessionStorage.setItem("launchSelectPrefill", JSON.stringify({
    mode: "sales_opportunity", customer_id: c.id, customer_name: c.name,
    customer_phone: c.phone, customer_email: c.email, customer_address: c.address,
  }));
  navigate("/admin/launch-select");
};

export default function Customers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/customers")).data); }
    catch { toast.error("Could not load customers."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    setRows((p) => p.filter((r) => r.id !== id));
    try { await api.delete(`/customers/${id}`); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); load(); }
  };

  const filtered = rows.filter((r) =>
    `${r.name} ${r.phone} ${r.email}`.toLowerCase().includes(q.toLowerCase()));

  if (profile) return <CustomerProfile customer={profile} onBack={() => setProfile(null)} />;

  return (
    <div className="space-y-4" data-testid="customers-module">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy">Customers <span className="text-slate-400 text-lg font-normal">({rows.length})</span></h1>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-cool" data-testid="customers-search" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" data-testid="customers-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
          <button onClick={() => setEditing({ ...BLANK })}
            className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-1.5 hover:opacity-90"
            style={{ background: PRIMARY }} data-testid="customers-add">
            <Plus className="w-4 h-4" /> Add Customer
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-16 text-sm">No customers yet — add your first one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Name</th>
                  <th className="text-left px-4 py-3 font-bold">Phone</th>
                  <th className="text-left px-4 py-3 font-bold">Email</th>
                  <th className="text-left px-4 py-3 font-bold">Address</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`customer-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      <button onClick={() => setProfile(r)} className="hover:text-[#F97316] hover:underline text-left" data-testid={`customer-open-${r.id}`}>{r.name}</button>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.phone}</td>
                    <td className="px-4 py-3 text-slate-600">{r.email}</td>
                    <td className="px-4 py-3 text-slate-500 truncate max-w-[220px]">{r.address}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => startOpportunity(r, navigate)} className="p-1.5 rounded hover:bg-orange-50" title="Start Sales Opportunity" data-testid={`customer-startopp-${r.id}`}><Rocket className="w-4 h-4 text-[#F97316]" /></button>
                        <button onClick={() => setProfile(r)} className="p-1.5 rounded hover:bg-slate-100" title="Timeline" data-testid={`customer-timeline-${r.id}`}><Activity className="w-4 h-4 text-[#F97316]" /></button>
                        <button onClick={() => setEditing(r)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`customer-edit-${r.id}`}><Pencil className="w-4 h-4 text-slate-500" /></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`customer-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <CustomerModal record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CustomerProfile({ customer, onBack }) {
  const navigate = useNavigate();
  return (
    <div data-testid="customer-profile">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-[#F97316] font-medium text-sm mb-5" data-testid="customer-profile-back">
        <ArrowLeft className="w-4 h-4" /> Back to Customers
      </button>
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy" data-testid="customer-profile-name">{customer.name}</h1>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-slate-600">
              {customer.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400" /> {customer.phone}</span>}
              {customer.email && <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-slate-400" /> {customer.email}</span>}
              {customer.address && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {customer.address}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {(customer.membership_plan || customer.lifetime_warranty) && (
              <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                {customer.lifetime_warranty ? "Lifetime Warranty" : customer.membership_plan}
              </span>
            )}
            <button onClick={() => startOpportunity(customer, navigate)} data-testid="customer-profile-startopp"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-bold hover:opacity-90" style={{ background: "#F97316" }}>
              <Rocket className="w-4 h-4" /> Start New Sales Opportunity
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-[#F97316]" />
          <h2 className="text-lg font-bold text-navy">Customer Timeline</h2>
          <span className="text-xs text-slate-400 font-medium">Single source of truth · every interaction, automatically</span>
        </div>
        <CustomerTimeline customerId={customer.id} customerName={customer.name} />
      </div>
    </div>
  );
}

function CustomerModal({ record, onClose, onSaved }) {
  const isEdit = !!record.id;
  const [f, setF] = useState({ ...BLANK, ...record });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/customers/${record.id}`, f);
      else await api.post("/customers", f);
      toast.success(isEdit ? "Updated." : "Added.");
      onSaved();
    } catch { toast.error("Save failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="customer-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{isEdit ? "Edit Customer" : "New Customer"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          <input autoFocus value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name *" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="customer-f-name" />
          <input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="customer-f-phone" />
          <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="customer-f-email" />
          <input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Address" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="customer-f-address" />
          <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes" rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="customer-f-notes" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60" style={{ background: PRIMARY }} data-testid="customer-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {isEdit ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
